import { Address } from "src/api/address/api";
import { DropPoint } from "src/api/locations";
import appState from "src/model/state";
import { RestrictedAddressCodes } from "src/model/state/partials/store.model";
import { EXPEDITION_SHIPPING_TYPE, ExpeditionShippingType } from "src/pages/Cart/BasketUserLoged/CheckoutShipping/CheckoutShipping.utils";
import { getCountryLanguage, getCurrentCountry, ISOCODEHandler } from "src/utils/BasePathTools";
import { ADDRESS_FLOW_TYPE, ROUTE_PATHS } from "./constants";
import { addCookieParamToUrl, isApp, isAppVersionLessThan } from "./utils";


export interface AddressSummary {
  name: string
  description:string 
}

export const INVALID_ADDRESS_VARIANT = {
  COUNTRY_INVALID:'country-invalid',
  ZIP_CODE_DISTRITE_INVALID:'zip-code-distrite-invalid'
} as any;

export type InvalidAddressVariant =
  (typeof INVALID_ADDRESS_VARIANT)[keyof typeof INVALID_ADDRESS_VARIANT];

const DEFAULT_IS_VALID_OBJECT = {valid: true, reason:''}

export const isAddressCountryValid = (address: Address) => {
  return (
    !address?.isIncomplete &&
    ISOCODEHandler(address?.countryCode) === ISOCODEHandler(getCurrentCountry())
  ) 
}

const isAddressZipCodeValid = (restrictedAddressCodes: RestrictedAddressCodes, address:Address) => {
  return  [...restrictedAddressCodes?.zipCodesRestricted].includes(address?.zipCode);
}

const isAddressStateValid = (restrictedAddressCodes : RestrictedAddressCodes, address:Address) => {
  return [...restrictedAddressCodes?.statesRestricted].includes(address?.stateCode);
}

export const isAddressZoneValid = (address: Address) => {
const { store:{restrictedAddressCodes} } = appState.get()
return isAddressZipCodeValid(restrictedAddressCodes, address) || isAddressStateValid(restrictedAddressCodes, address)
}

export function isValidAddresForSelected(address: Address) {
  if (!isAddressCountryValid(address)) {
    return {...DEFAULT_IS_VALID_OBJECT, valid:false, reason: INVALID_ADDRESS_VARIANT.COUNTRY_INVALID }
  } else if(isAddressZoneValid(address)) {
    return {...DEFAULT_IS_VALID_OBJECT, valid:false, reason: INVALID_ADDRESS_VARIANT.ZIP_CODE_DISTRITE_INVALID }
  } else {
    return DEFAULT_IS_VALID_OBJECT
  }
}

export function isValidAddress(address: Address) {
  return isAddressCountryValid(address)
}

export function getAddresFlowType() {
  const currentFlow = location?.pathname.match(/\/([^\/]*)$/)[1] as any;
  const currentType =
    currentFlow &&
    [
      ADDRESS_FLOW_TYPE.PAYMENT_METHOD_ADDRESS,
      ADDRESS_FLOW_TYPE.SENDER_ADDRESS,
      ADDRESS_FLOW_TYPE.SHIPPING_ADDRESS,
      ADDRESS_FLOW_TYPE.CART_ADDRES,
    ].find((type) => type === currentFlow);

  return currentType;
}

export const getAddressDescription = (address: Address, isAddressWithoutPhoneNumber?: boolean): string => {
  if (address) {
    const phoneExtraction =
      address?.phones?.length > 0
        ? address.phones[0]
        : { countryCode: "", subscriberNumber: "" };

    const phoneDescription = `${phoneExtraction?.countryCode} ${phoneExtraction?.subscriberNumber}`;
    const addressDescription = [...address?.addressLines]
      .filter(Boolean)
      .join(" ");

    return [
      addressDescription,
      address?.city,
      address?.country,
      isAddressWithoutPhoneNumber ? "" : phoneDescription,
    ]
      .filter(Boolean)
      .join(", ");
  } else {
    return "";
  }
};

export const getBasketDefaultAddress = (addresses: Address[]) => {
  const validAddresses = addresses.filter((addr) => isValidAddress(addr) && !isAddressZoneValid(addr));
  return validAddresses.find((addr) => addr?.isPreferred) ?? !!validAddresses?.length ? validAddresses[0] : null;
};

export const getAddressDataById = (addresses: Address[], id: number) => {
  const returnAddress = addresses?.find(
    (address: Address) => Number(address?.id) === Number(id),
  );
  return returnAddress ?? null;
};

export const getAddressName = (address: Address): string => {
  return [address?.firstName, address?.lastName].filter(Boolean).join(" ");
};

export const getDropPointName = (dropPointAddres: DropPoint) => {
  return dropPointAddres?.pickUpPoint?.destinationName ?? "";
}

export const getDropPointDescription = (dropPoint?: DropPoint): string => {
  if (!dropPoint) {
    return '';
  }
  
  const addressLine = dropPoint.addressLines && dropPoint.addressLines.length > 0 
    ? dropPoint.addressLines[0] 
    : '';
    
  const city = dropPoint.city || '';
  const zipCode = dropPoint.zipCode || '';
  
  const pickUpPointName = dropPoint.pickUpPoint?.destinationName || '';
  
  const parts = [];
  
  if (pickUpPointName) {
    parts.push(pickUpPointName);
  }
  
  if (addressLine) {
    parts.push(addressLine);
  }
  
  if (city) {
    parts.push(city);
  }
  
  if (zipCode) {
    parts.push(zipCode);
  }
  
  return parts.join(', ');
};

export const getAddressSummary = (address, shippingType: ExpeditionShippingType): AddressSummary => {
  if(shippingType === EXPEDITION_SHIPPING_TYPE.DROP2HOME){
    return { name: getAddressName(address[shippingType]), description: getAddressDescription(address[shippingType], true)}
  }else{
    return { name: getDropPointName(address[shippingType]), description: getDropPointDescription(address[shippingType])}
  }
}

export const goToZaraAddress = (originUrl: string): void => {
  const profileUrl = `${window.location.origin}${getCountryLanguage()}preowned-resell${ROUTE_PATHS.PROFILE}`;
  const returnUrl = originUrl ? originUrl : profileUrl;
  const url = addCookieParamToUrl(returnUrl);
  const URL = `${window.location.origin}${getCountryLanguage()}reselling/addresses?returnUrl=${encodeURIComponent(url)}`;

  location.replace(URL);
};

export const validateAddress = (address: Address) => { 
    if(address?.isBilling && address?.isIncomplete) {
      return false
    } if(isAddressCountryValid(address) && !isAddressZoneValid(address)) {
      return true
    } else{
      return false
    }
  }